import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vehiclesApi, vehicleDispatchApi, fuelApi, maintenanceApi } from '../../api/fleet';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { useToast } from '../../components/Toast';
import { EmployeePicker } from '../../components/EmployeePicker';
import { VEHICLE_STATUS_LABELS } from './VehiclesListPage';
import { VEHICLE_STAGES } from '../../config/workflow-stages';
import { formatDate, formatDateTime } from '../../utils/formatDate';

export function VehicleWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const vehicleId = Number(id);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: vehicleData, isError } = useQuery({ queryKey: ['vehicle', vehicleId], queryFn: () => vehiclesApi.get(vehicleId) });
  const { data: docsData } = useQuery({ queryKey: ['vehicle-documents', vehicleId], queryFn: () => genericDocumentApi.listFor('vehicle', vehicleId) });
  const vehicle = vehicleData?.data;

  const { data: dispatchesData } = useQuery({ queryKey: ['vehicle-dispatches', vehicleId], queryFn: () => vehiclesApi.listDispatches(vehicleId) });
  const dispatches = dispatchesData?.data ?? [];
  const activeDispatch = dispatches.find((d) => d.status !== 'returned');

  const [dispatchForm, setDispatchForm] = useState({ driverEmployeeId: '', destination: '', purpose: '', departureDatetime: '', openingOdometer: '' });
  const createDispatchMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => vehicleDispatchApi.create(input),
    onSuccess: () => { showSuccess('Vehicle dispatched.'); queryClient.invalidateQueries({ queryKey: ['vehicle-dispatches', vehicleId] }); queryClient.invalidateQueries({ queryKey: ['vehicle', vehicleId] }); },
    onError: (err: ApiError) => showError(err.message),
  });
  const returnDispatchMutation = useMutation({
    mutationFn: ({ dispatchId, closingOdometer }: { dispatchId: number; closingOdometer: number }) =>
      vehicleDispatchApi.recordReturn(dispatchId, { returnDatetime: new Date().toISOString().slice(0, 19).replace('T', ' '), closingOdometer }),
    onSuccess: (result) => { showSuccess(`Return recorded - ${result.data.kilometresTravelled} km travelled.`); queryClient.invalidateQueries({ queryKey: ['vehicle-dispatches', vehicleId] }); queryClient.invalidateQueries({ queryKey: ['vehicle', vehicleId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  const { data: fuelData } = useQuery({ queryKey: ['vehicle-fuel', vehicleId], queryFn: () => vehiclesApi.listFuel(vehicleId) });
  const fuelTransactions = fuelData?.data ?? [];
  const { data: consumptionData } = useQuery({ queryKey: ['vehicle-fuel-consumption', vehicleId], queryFn: () => vehiclesApi.getFuelConsumption(vehicleId) });
  const [fuelForm, setFuelForm] = useState({ transactionType: 'purchase', quantityLitres: '', totalCost: '', transactionDate: '' });
  const createFuelMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => fuelApi.create({ ...input, vehicleId }),
    onSuccess: () => { showSuccess('Fuel transaction recorded.'); queryClient.invalidateQueries({ queryKey: ['vehicle-fuel', vehicleId] }); queryClient.invalidateQueries({ queryKey: ['vehicle-fuel-consumption', vehicleId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  const { data: maintenanceData } = useQuery({ queryKey: ['vehicle-maintenance', vehicleId], queryFn: () => vehiclesApi.listMaintenance(vehicleId) });
  const maintenanceRecords = maintenanceData?.data ?? [];
  const [maintenanceForm, setMaintenanceForm] = useState({ maintenanceType: 'repair', issueDescription: '', requestDate: '' });
  const createMaintenanceMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => maintenanceApi.create({ ...input, vehicleId }),
    onSuccess: () => { showSuccess('Maintenance requested.'); queryClient.invalidateQueries({ queryKey: ['vehicle-maintenance', vehicleId] }); queryClient.invalidateQueries({ queryKey: ['vehicle', vehicleId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  if (isError) return <div className="page"><div className="error-state">Failed to load this vehicle. Please try refreshing the page.</div></div>;
  if (!vehicle) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  return (
    <div className="page">
      <RecordHeader
        title={vehicle.registration_number}
        readOnly={false}
        entityType="vehicle"
        entityId={vehicleId}
        fields={[
          { label: 'Make/Model', value: `${vehicle.make ?? ''} ${vehicle.model ?? ''}`.trim() || vehicle.vehicle_reference },
          { label: 'Odometer', value: vehicle.current_odometer },
          { label: 'Status', value: <StatusBadge status={vehicle.status} label={VEHICLE_STATUS_LABELS[vehicle.status]} /> },
        ]}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'overview', label: 'Overview', content: (
              <div>
                <SectionHeader title="Vehicle Information" />
                <dl className="detail-grid">
                  <dt>Fuel Type</dt><dd>{vehicle.fuel_type}</dd>
                  <dt>Ownership</dt><dd>{vehicle.ownership_type}</dd>
                  <dt>Duty Station</dt><dd>{vehicle.duty_station ?? '-'}</dd>
                  <dt>Insurance Expiry</dt><dd>{vehicle.insurance_expiry ? formatDate(vehicle.insurance_expiry) : '-'}</dd>
                  <dt>Registration Expiry</dt><dd>{vehicle.registration_expiry ? formatDate(vehicle.registration_expiry) : '-'}</dd>
                  <dt>Roadworthiness Expiry</dt><dd>{vehicle.roadworthiness_expiry ? formatDate(vehicle.roadworthiness_expiry) : '-'}</dd>
                  <dt>Next Service</dt><dd>{vehicle.next_service_date ? formatDate(vehicle.next_service_date) : '-'}</dd>
                </dl>
              </div>
            ),
          },
          {
            key: 'dispatch', label: 'Dispatch / Trip Log', content: (
              <div>
                <SectionHeader title="Trip History" />
                <ul>
                  {dispatches.map((d) => (
                    <li key={d.id}>
                      {d.destination} · departed {formatDateTime(d.departure_datetime)}, opening {d.opening_odometer}
                      {d.return_datetime ? ` · returned ${formatDateTime(d.return_datetime)}, closing ${d.closing_odometer}` : (
                        <> · <button onClick={() => { const km = prompt('Closing odometer?'); if (km) returnDispatchMutation.mutate({ dispatchId: d.id, closingOdometer: Number(km) }); }}>Record Return</button></>
                      )}
                    </li>
                  ))}
                </ul>
                {!activeDispatch && (
                  <form className="inline-form" onSubmit={(e) => {
                    e.preventDefault();
                    createDispatchMutation.mutate({ vehicleId, driverEmployeeId: Number(dispatchForm.driverEmployeeId), destination: dispatchForm.destination, purpose: dispatchForm.purpose, departureDatetime: dispatchForm.departureDatetime, openingOdometer: Number(dispatchForm.openingOdometer) });
                  }}>
                    <EmployeePicker value={dispatchForm.driverEmployeeId} onChange={(e) => setDispatchForm((s) => ({ ...s, driverEmployeeId: e.target.value }))} />
                    <input placeholder="Destination" value={dispatchForm.destination} onChange={(e) => setDispatchForm((s) => ({ ...s, destination: e.target.value }))} required />
                    <input placeholder="Purpose" value={dispatchForm.purpose} onChange={(e) => setDispatchForm((s) => ({ ...s, purpose: e.target.value }))} required />
                    <input type="datetime-local" value={dispatchForm.departureDatetime} onChange={(e) => setDispatchForm((s) => ({ ...s, departureDatetime: e.target.value }))} required />
                    <input type="number" step="0.1" placeholder="Opening odometer" value={dispatchForm.openingOdometer} onChange={(e) => setDispatchForm((s) => ({ ...s, openingOdometer: e.target.value }))} required />
                    <button type="submit" disabled={createDispatchMutation.isPending}>Dispatch</button>
                  </form>
                )}
              </div>
            ),
          },
          {
            key: 'fuel', label: 'Fuel', content: (
              <div>
                <SectionHeader title="Fuel Consumption" />
                {consumptionData?.data && <p className="muted">{consumptionData.data.total_litres ?? 0} litres total, {consumptionData.data.transaction_count} transactions.</p>}
                <ul>
                  {fuelTransactions.map((f) => (
                    <li key={f.id}>{formatDate(f.transaction_date)} · {f.quantity_litres}L · {f.total_cost ? `NGN ${f.total_cost}` : '-'} {f.is_flagged_for_review ? '⚠ Flagged' : ''}</li>
                  ))}
                </ul>
                <form className="inline-form" onSubmit={(e) => {
                  e.preventDefault();
                  createFuelMutation.mutate({ transactionType: fuelForm.transactionType, quantityLitres: Number(fuelForm.quantityLitres), totalCost: fuelForm.totalCost ? Number(fuelForm.totalCost) : undefined, transactionDate: fuelForm.transactionDate });
                }}>
                  <select value={fuelForm.transactionType} onChange={(e) => setFuelForm((s) => ({ ...s, transactionType: e.target.value }))}>
                    <option value="purchase">Purchase</option><option value="issuance">Issuance</option><option value="fuel_card">Fuel Card</option><option value="voucher">Voucher</option>
                  </select>
                  <input type="number" step="0.1" placeholder="Litres" value={fuelForm.quantityLitres} onChange={(e) => setFuelForm((s) => ({ ...s, quantityLitres: e.target.value }))} required />
                  <input type="number" step="0.01" placeholder="Total cost" value={fuelForm.totalCost} onChange={(e) => setFuelForm((s) => ({ ...s, totalCost: e.target.value }))} />
                  <input type="date" value={fuelForm.transactionDate} onChange={(e) => setFuelForm((s) => ({ ...s, transactionDate: e.target.value }))} required />
                  <button type="submit" disabled={createFuelMutation.isPending}>Record</button>
                </form>
              </div>
            ),
          },
          {
            key: 'maintenance', label: 'Maintenance', content: (
              <div>
                <SectionHeader title="Maintenance History" />
                <ul>
                  {maintenanceRecords.map((m) => (
                    <li key={m.id}>{m.maintenance_reference} · {m.issue_description} · <StatusBadge status={m.status} /></li>
                  ))}
                </ul>
                <form className="inline-form" onSubmit={(e) => { e.preventDefault(); createMaintenanceMutation.mutate(maintenanceForm); }}>
                  <select value={maintenanceForm.maintenanceType} onChange={(e) => setMaintenanceForm((s) => ({ ...s, maintenanceType: e.target.value }))}>
                    <option value="preventive">Preventive</option><option value="scheduled_service">Scheduled Service</option><option value="repair">Repair</option>
                    <option value="tyre_replacement">Tyre Replacement</option><option value="battery_replacement">Battery Replacement</option><option value="inspection">Inspection</option>
                    <option value="accident_repair">Accident Repair</option><option value="emergency_repair">Emergency Repair</option>
                  </select>
                  <input placeholder="Issue description" value={maintenanceForm.issueDescription} onChange={(e) => setMaintenanceForm((s) => ({ ...s, issueDescription: e.target.value }))} required />
                  <input type="date" value={maintenanceForm.requestDate} onChange={(e) => setMaintenanceForm((s) => ({ ...s, requestDate: e.target.value }))} required />
                  <button type="submit" disabled={createMaintenanceMutation.isPending}>Request Maintenance</button>
                </form>
              </div>
            ),
          },
          {
            key: 'documents', label: 'Documents', content: (
              <DocumentsSection
                documents={docsData?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('vehicle', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('vehicle', vehicleId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('vehicle', documentId)}
                queryKeyToInvalidate={['vehicle-documents', vehicleId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking', label: 'Tracking', content: (
              <div>
                <WorkflowTrackingTab entityType="vehicle" entityId={vehicleId} actionStageMap={VEHICLE_STAGES} statusLabels={VEHICLE_STATUS_LABELS} />
                <CommentsThread entityType="vehicle" entityId={vehicleId} section="fleet" heading="Comments for this Vehicle" />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
